import { FC, useState, useMemo } from "react";
import { Pagination } from "@mantine/core";
import { useDebounce } from "react-use";
import { usePage } from "hooks/usePage";
import PageHeader from "components/layout/page-header";
import PageWrapper from "components/layout/page-wrapper";
import TableWithCheckbox from "components/TableWithCheckbox/TableWithCheckbox";
import { useQuery } from "@apollo/client";
import { fetchHoldKavaOffers } from "graphql/queries/holdOffers";
import { getHoldOfferColumns } from "./columns";

const HoldOffersPage: FC = () => {
  usePage("Kava | Hold Cover");

  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
    },
    1000,
    [searchValue]
  );

  const { data, loading, error, refetch } = useQuery(fetchHoldKavaOffers, {
    variables: {
      input: {},
    },
    fetchPolicy: "network-only",
  });

  const offers = data?.fetchHoldKavaOffers || [];

  const filteredOffers = offers.filter((offer: any) => {
    if (!debouncedSearchValue) return true;

    const searchLower = debouncedSearchValue.toLowerCase();
    const policyNumber = offer?.offer_detail?.policy_number?.toLowerCase() || "";
    const insuredBy = offer?.offer_detail?.insured_by?.toLowerCase() || "";
    const businessName = offer?.classofbusiness?.business_name?.toLowerCase() ||
                        offer?.class_of_business?.business_name?.toLowerCase() || "";

    return (
      policyNumber.includes(searchLower) ||
      insuredBy.includes(searchLower) ||
      businessName.includes(searchLower)
    );
  });

  const columns = useMemo(
    () => getHoldOfferColumns(refetch, filteredOffers),
    [refetch, filteredOffers]
  );

  const totalOffers = filteredOffers.length;
  const from = totalOffers === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalOffers);
  const lastPage = Math.ceil(totalOffers / perPage);

  return (
    <PageWrapper>
      <PageHeader title="Hold Covers" />

      <section className="mt-8" aria-labelledby="hold-offers-heading">
        <TableWithCheckbox
          columns={columns}
          data={filteredOffers}
          searchValue={searchValue}
          search={(value: string) => setSearchValue(value)}
          setSelected={() => {}}
          selected={[]}
          loading={loading}
          error={error}
          refresh={refetch}
          setNumberOfRows={setPerPage}
          numberOfRows={perPage}
          searchPlaceholder="Search hold covers..."
        />
      </section>

      <section className="p-3 flex justify-between items-center mt-6">
        <span>
          Showing {from} to {to} of {totalOffers} items
        </span>
        {lastPage > 1 && (
          <Pagination
            total={lastPage}
            page={page}
            onChange={(page) => {
              setPage(page);
            }}
          />
        )}
      </section>
    </PageWrapper>
  );
};

export default HoldOffersPage;
